import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ORG_SELECTOR_DROPDOWN_CONTENT_ID, ORG_SWITCHER_DROPDOWN_CONTENT_ID } from '@ui/constants';
import { generateOrgColor, getInitials } from '@ui/primitives';
import { Avatar, AvatarFallback } from '@ui/primitives/avatar';
import { Button } from '@ui/primitives/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@ui/primitives/dropdown-menu';
import { ChevronsUpDown } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useGetOrganization, useGetOrganizationCounters } from 'ze-sdk';
import { OrgSwitcherOptions } from './org-switcher-options';
const subscriptionNameMap = {
    PERSONAL: 'Personal Plan',
    TEAM: 'Team Plan',
    PRO: 'Business Plan',
    ENTERPRISE: 'Enterprise Plan'
};
export function OrgSwitcher(param) {
    let { selectedOrg, organizations, onOrgChange, onClose, orgSubscriptionTier, isOpen, onOpen } = param;
    var _ref, _ref1, _ref2, _ref3, _ref4, _ref5;
    // Get detailed organization data for the trigger display
    const { data: orgRes } = useGetOrganization({
        organization: selectedOrg !== null && selectedOrg !== void 0 ? selectedOrg : ''
    });
    const orgData = orgRes === null || orgRes === void 0 ? void 0 : orgRes.value;
    const { data: countersRes } = useGetOrganizationCounters({
        organization: selectedOrg !== null && selectedOrg !== void 0 ? selectedOrg : ''
    });
    const orgCounters = countersRes === null || countersRes === void 0 ? void 0 : countersRes.value;
    const { currentOrg } = useMemo(()=>{
        const currentOrgRes = organizations.find((org)=>org.name === selectedOrg);
        return {
            currentOrg: currentOrgRes !== null && currentOrgRes !== void 0 ? currentOrgRes : null
        };
    }, [
        organizations,
        selectedOrg
    ]);
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            // Cmd+Shift+O (macOS) or Ctrl+Shift+O (Windows/Linux) to open org switcher
            if ((event.metaKey || event.ctrlKey) && event.shiftKey && (event.key === 'O' || event.key === 'o')) {
                event.preventDefault();
                event.stopPropagation();
                onOpen(true);
                return;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return ()=>{
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    const orgAvatarColor = generateOrgColor((_ref = orgData === null || orgData === void 0 ? void 0 : orgData.name) !== null && _ref !== void 0 ? _ref : '');
    useEffect(()=>{
        if (!isOpen) {
            return;
        }
        const handleScroll = (event)=>{
            const orgSwitcherDropdown = document.getElementById(ORG_SWITCHER_DROPDOWN_CONTENT_ID);
            const orgSelectorDropdown = document.getElementById(ORG_SELECTOR_DROPDOWN_CONTENT_ID);
            if (!orgSwitcherDropdown) {
                onOpen(false);
                return;
            }
            const isWithinSwitcher = isEventWithinDropdown(event, orgSwitcherDropdown);
            const isWithinSelector = orgSelectorDropdown ? isEventWithinDropdown(event, orgSelectorDropdown) : false;
            if (isWithinSwitcher || isWithinSelector) {
                return;
            }
            onOpen(false);
        };
        window.addEventListener('scroll', handleScroll, true);
        return ()=>{
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [
        isOpen,
        onOpen
    ]);
    const isSingleOrg = organizations.length < 2;
    return /*#__PURE__*/ _jsx("div", {
        id: "org-switcher-dropdown-trigger",
        className: "flex items-center gap-3 w-full h-18 p-3",
        "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_SUMMARY",
        children: /*#__PURE__*/ _jsxs(DropdownMenu, {
            open: isOpen,
            onOpenChange: onOpen,
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                    "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_CURRENT_ORG_TRIGGER",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "cursor-pointer flex items-center gap-3 w-full",
                        children: [
                            /*#__PURE__*/ _jsx(Avatar, {
                                className: "w-10 h-10 rounded-md ",
                                children: /*#__PURE__*/ _jsx(AvatarFallback, {
                                    className: 'text-sm font-semibold rounded-md',
                                    style: {
                                        backgroundColor: orgAvatarColor
                                    },
                                    children: getInitials((_ref1 = currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.name) !== null && _ref1 !== void 0 ? _ref1 : '')
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 min-w-0 text-left",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "font-semibold text-foreground truncate",
                                        children: (_ref2 = (_ref3 = currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.displayName) !== null && _ref3 !== void 0 ? _ref3 : currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.name) !== null && _ref2 !== void 0 ? _ref2 : 'Select Organization'
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-sm text-muted-foreground",
                                        children: [
                                            subscriptionNameMap[orgSubscriptionTier],
                                            " \xb7 ",
                                            (_ref4 = orgCounters === null || orgCounters === void 0 ? void 0 : orgCounters.projects) !== null && _ref4 !== void 0 ? _ref4 : 0,
                                            " projects \xb7",
                                            ' ',
                                            (_ref5 = orgCounters === null || orgCounters === void 0 ? void 0 : orgCounters.applications) !== null && _ref5 !== void 0 ? _ref5 : 0,
                                            " apps"
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                    "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_TRIGGER",
                    children: /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "icon",
                        className: "outline-none p-0 cursor-pointer rounded-lg ring-1 ring-muted-foreground/50",
                        "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_BUTTON",
                        children: /*#__PURE__*/ _jsx(ChevronsUpDown, {
                            className: "w-5 h-5 text-muted-foreground"
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(DropdownMenuContent, {
                    id: ORG_SWITCHER_DROPDOWN_CONTENT_ID,
                    "data-e2e": "LAYOUT_HEADER__ORG_SWITCHER_OPTIONS_DROPDOWN",
                    className: "w-[--radix-dropdown-menu-trigger-width] min-w-72 flex flex-col p-0 border-l-0 rounded-tl-none border-1 border-[var(--border)] shadow-lg ".concat(isSingleOrg ? 'rounded-bl-none' : ''),
                    align: "start",
                    side: "right",
                    sideOffset: 16,
                    alignOffset: -23,
                    collisionPadding: 16,
                    avoidCollisions: false,
                    onInteractOutside: (event)=>{
                        const target = event.target;
                        if (target === null || target === void 0 ? void 0 : target.closest("#".concat(ORG_SELECTOR_DROPDOWN_CONTENT_ID))) {
                            event.preventDefault();
                        }
                    },
                    onPointerDownOutside: (event)=>{
                        const target = event.target;
                        if (target === null || target === void 0 ? void 0 : target.closest("#".concat(ORG_SELECTOR_DROPDOWN_CONTENT_ID))) {
                            event.preventDefault();
                        }
                    },
                    children: /*#__PURE__*/ _jsx(OrgSwitcherOptions, {
                        organizations: organizations,
                        onOrgChange: onOrgChange,
                        onClose: ()=>{
                            onOpen(false);
                            onClose === null || onClose === void 0 ? void 0 : onClose();
                        }
                    })
                })
            ]
        })
    });
}
const isEventWithinDropdown = (event, element)=>{
    const target = event.target;
    if (target && element.contains(target)) {
        return true;
    }
    return event.composedPath().some((node)=>node === element);
};
